/* eslint-disable no-plusplus */
/* eslint-disable no-undef */

import { MessageBus } from '@podium/browser';
import {
    setNotificationsUnreadCount,
    setMessagesUnreadCount,
    setProfileImage,
    storeNotificationsUnreadCount,
    storeMessagesUnreadCount,
    storeProfileImage,
} from './utils';

// add a flag to indicate the the script has been loaded
// we use this to check whether we need to manually load the script
// Necessary for some Java apps like innfinn
window.headerLoaded = true;

class Topbar extends HTMLElement {
    constructor() {
        super();

        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML += `
            <link
                rel="stylesheet"
                type="text/css"
                href="https://assets.finn.no/pkg/@fabric-ds/css/v1/fabric.min.css"
                onload="(function(){ const event = new Event('header-load'); document.dispatchEvent(event); })(this)"
            />
            <style>
                :host {
                    display: block;
                }
                @media print {
                    header { position: static !important; }
                }
            </style>
            ${this.innerHTML}
        `;

        this.innerHTML = '';
        this.setup();
    }

    setup() {
        const {
            dataset: { spidId, loginId, apiUrl, env, distractionFree },
        } = this.shadowRoot.querySelector('#topbar_api-url');

        const suffix = env === 'prod' ? '' : 'Staging';
        const pulse2opt = {
            clientId: `finn${suffix}`,
            userIdDomain: 'spid.no',
        };
        window.pulse2opt = window.pulse2opt || pulse2opt;
        window.pulse2opt.userId = spidId;

        window.finnUserId = loginId;

        if (!spidId || !loginId) return;

        if (distractionFree === 'true') return;

        const store = window.sessionStorage;
        const cachedNotificationsUnreadCount = store.getItem(
            'notificationsUnreadCount',
        );
        const cachedMessagesUnreadCount = store.getItem('messagesUnreadCount');
        const cachedProfileImageURL = store.getItem('profileImageURL');

        if (cachedNotificationsUnreadCount && loginId && spidId) {
            setNotificationsUnreadCount(
                cachedNotificationsUnreadCount,
                this.shadowRoot,
            );
        }
        if (cachedMessagesUnreadCount && loginId && spidId) {
            setMessagesUnreadCount(cachedMessagesUnreadCount, this.shadowRoot);
        }
        if (cachedProfileImageURL && loginId && spidId) {
            setProfileImage(cachedProfileImageURL, this.shadowRoot);
        }

        fetch(new URL(apiUrl), { credentials: 'include' })
            .then((res) => {
                if (res.ok) {
                    res.json()
                        .then((result) => {
                            const {
                                notificationsUnreadCount,
                                messagesUnreadCount,
                                profileImage,
                            } = result;

                            if (
                                cachedNotificationsUnreadCount !==
                                notificationsUnreadCount
                            ) {
                                setNotificationsUnreadCount(
                                    notificationsUnreadCount,
                                    this.shadowRoot,
                                );
                                storeNotificationsUnreadCount(
                                    notificationsUnreadCount,
                                );
                            }

                            if (
                                cachedMessagesUnreadCount !==
                                messagesUnreadCount
                            ) {
                                setMessagesUnreadCount(
                                    messagesUnreadCount,
                                    this.shadowRoot,
                                );
                                storeMessagesUnreadCount(messagesUnreadCount);
                            }

                            if (cachedProfileImageURL !== profileImage) {
                                setProfileImage(profileImage, this.shadowRoot);
                                storeProfileImage(profileImage);
                            }
                        })
                        .catch(() => {
                            // noop
                        });
                } else if (res.status === 401) {
                    // not logged in, clear caches and return
                    setNotificationsUnreadCount(0, this.shadowRoot);
                    storeNotificationsUnreadCount(0);
                    setMessagesUnreadCount(0, this.shadowRoot);
                    storeMessagesUnreadCount(0);
                    setProfileImage(null, this.shadowRoot);
                    storeProfileImage(null);
                }

                const messageBus = new MessageBus();
                messageBus.subscribe('notifications', 'allWereViewed', () => {
                    if (typeof window !== 'undefined') {
                        const redDot = this.shadowRoot.querySelector(
                            '#notifications-bubble',
                        );

                        if (redDot) {
                            redDot.remove();
                        }
                    }
                });
            })
            .catch(() => {
                // noop
            });
    }
}

customElements.define('finn-topbar', Topbar);

/**
 * Render broadcast warnings for unsupported browsers
 */
document.addEventListener('DOMContentLoaded', () => {
    try {
        // the warning will only be rendered if the page has an element with an id of `broadcast`
        // this is to ensure we render in the correct place, and don't mess with someones layout
        const container = document.querySelector('#broadcast');

        if (!container) {
            return;
        }

        // Check unleash toggle
        if (!document.querySelector(['header[data-browser-warning="true"]'])) {
            return;
        }

        // See if the browser supports CSS custom properties https://stackoverflow.com/a/38012166/966362
        if (window.CSS && window.CSS.supports('color', 'var(--detect)')) {
            return;
        }

        const storageKey = 'dismissedBrowserVersionWarning';

        // Make sure we don't rerender on the next pages if the user has already dimissed it
        const hasDismissed =
            window.sessionStorage.getItem(storageKey) === 'true';

        if (hasDismissed) return;

        const broadcastElement = document.createElement('section');
        broadcastElement.className = 'broadcast';
        broadcastElement.setAttribute('aria-label', 'Viktig melding');

        // Message
        const content = document.createElement('div');
        content.className = 'broadcast__content';
        content.innerHTML = `
            <p>Hei! Vi ser at du bruker en eldre nettleser. Det kan derfor hende at FINN ikke fungerer optimalt for deg.</p>
            <p>Fra 1. januar 2021 kan det hende at FINN ikke vil fungere for deg i det hele tatt, ettersom vi ikke lenger kommer til å støtte gamle versjoner av nettlesere.</p>
            <p>Vi anbefaler at du oppdaterer nettleseren eller enheten din til siste versjon. Eller hva med å teste en ny nettleser? Vi har noen forslag 
                <a class="u-display-block u-mt16" href="https://www.google.com/intl/no/chrome/">Google Chrome</a>
                <a class="u-display-block u-mt8" href="https://www.microsoft.com/nb-no/edge">Microsoft Edge</a>
                <a class="u-display-block u-mt8" href="https://www.mozilla.org/nb-NO/firefox/new/">Mozilla Firefox</a>
            </p>
                `;

        broadcastElement.appendChild(content);
        // end Message

        // Button
        const icon = document.createElement('span');
        icon.className = 'icon icon--cross';

        const dismissButtonElement = document.createElement('button');
        dismissButtonElement.className =
            'button button--pill button--small button--has-icon broadcast__dismiss';
        dismissButtonElement.type = 'button';
        dismissButtonElement.setAttribute('aria-label', 'Lukk');

        const listener = () => {
            broadcastElement.remove();
            dismissButtonElement.removeEventListener('click', listener);
            window.sessionStorage.setItem(storageKey, true);
        };

        dismissButtonElement.addEventListener('click', listener);
        dismissButtonElement.appendChild(icon);

        broadcastElement.appendChild(dismissButtonElement);
        // End button

        container.appendChild(broadcastElement);
    } catch (e) {
        // noop
    }
});
